import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../layouts/AdminLayout";
import { API_BASE_URL, fetchData } from "../../api/api";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  CONSTANT_CURRENCY,
  CONSTANT_REDIRECT_TIME,
  GOOGLE_API_KEY,
  GOOGLE_MAP_LIBRARIES,
} from "../../constant/common";
import api from "../../api/api";
import { decryptData, encryptData } from "../../utils/encryptDecrypt";
import Spinner from "../../components/common/Spinner";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import getCroppedImg from "../../utils/cropImage";
import { AuthContext } from "../../context/AuthContext";
import PaymentModal from "../../components/admin/practices/PaymentModal";

const AdminPracticesEdit = () => {
  const [isPaymentModal, setIsPaymentModal] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);

  const [suburbPostcodeStateList, setSuburbPostcodeStateList] = useState([]);
  const [selectedSuburbPostcodeState, setSelectedSuburbPostcodeState] =
    useState(null);

  const [
    suburbWithPostcodeStateSearchInput,
    setSuburbWithPostcodeStateSearchInput,
  ] = useState("");
  const [
    suburbWithPostcodeStateFilteredOptions,
    setSuburbWithPostcodeStateFilteredOptions,
  ] = useState([]);

  const autocompleteRef = useRef(null);
  const autocompleteInstance = useRef(null);

  useEffect(() => {
    if (suburbPostcodeStateList?.length === 0) {
      console.warn("Suburb list is empty");
      return;
    }

    const intervalId = setInterval(() => {
      if (!autocompleteRef.current) {
        console.warn("Waiting for input field to render...");
        return;
      }
      clearInterval(intervalId);

      if (!(autocompleteRef.current instanceof HTMLInputElement)) {
        console.error(
          "autocompleteRef is not an input element",
          autocompleteRef.current
        );
        return;
      }

      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error("Google Maps Places API not available");
        return;
      }

      // Initialize Google Autocomplete
      autocompleteInstance.current = new window.google.maps.places.Autocomplete(
        autocompleteRef.current,
        { componentRestrictions: { country: "AU" } }
      );

      // Handle place selection
      autocompleteInstance.current.addListener("place_changed", () => {
        const place = autocompleteInstance.current.getPlace();
        if (!place || !place.geometry) return;

        const locationData = {
          name: place.name || "",
          lat: place.geometry?.location?.lat() || null,
          lng: place.geometry?.location?.lng() || null,
          suburb: null,
          state: null,
          postcode: null,
          place_id: place.place_id || null,
          url: place.url || null,
        };

        place.address_components?.forEach((component) => {
          if (component.types.includes("postal_code")) {
            locationData.postcode = component.short_name;
          }
          if (component.types.includes("locality")) {
            locationData.suburb = component.short_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            locationData.state = component.short_name;
          }
        });

        // autocompleteRef.current.value = locationData.name;

        const formattedLocation =
          `${locationData.suburb}, ${locationData.state} ${locationData.postcode}`.trim();

        // console.log(formattedLocation)

        const matchedSuburb = suburbPostcodeStateList.find((suburb) => {
          const formattedName =
            `${suburb.name}, ${suburb.state_name} ${suburb.postcode}`.trim();
          return (
            formattedLocation.toLowerCase() === formattedName.toLowerCase()
          );
        });

        // console.log("matchedSuburb : " + JSON.stringify(matchedSuburb));

        if (matchedSuburb) {
          setSelectedSuburbPostcodeState(matchedSuburb);
        } else {
          setSelectedSuburbPostcodeState(null);
        }

        setFormData((prevState) => ({
          ...prevState,
          address: locationData.name,
          longitude: locationData.lng,
          latitude: locationData.lat,
          place_id: locationData.place_id,
          address_url: locationData.url,
          fk_states_id: matchedSuburb?.states_id,
          fk_postcodes_id: matchedSuburb?.postcodes_id,
          fk_suburbs_id: matchedSuburb?.suburbs_id,
        }));
      });
    }, 100);

    return () => clearInterval(intervalId);
  }, [suburbPostcodeStateList]);

  const { user } = useContext(AuthContext);

  const { practices_id } = useParams();

  const decrypt_practices_id = decryptData(practices_id);

  const [practiceDetails, setPracticeDetails] = useState([]);

  const getPracticeDetails = async () => {
    setIsLoading(true);
    try {
      const data = await fetchData("/detail-practices", {
        practices_id: decrypt_practices_id,
      });

      setPracticeDetails(data);

      setSelectedSuburbPostcodeState({
        suburbs_id: data?.suburb?.suburbs_id,
        name: data?.suburb?.name,
        postcodes_id: data?.postcode?.postcodes_id,
        postcode: data?.postcode?.postcode,
        states_id: data?.state?.states_id,
        state_name: data?.state?.name,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const navigate = useNavigate();

  const didFetch = useRef(false);

  const [isLoading, setIsLoading] = useState(false);

  const [planList, setPlanList] = useState([]);
  const [planFeaturesList, setPlanFeaturesList] = useState([]);

  const [stateList, setStateList] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [paymentMethodsList, setPaymentMethodsList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [postCodesList, setPostCodesList] = useState([]);
  const [suburbList, setSuburbList] = useState([]);

  const [isServicesList, setIsServicesList] = useState(false);
  const [isPaymentMethodsList, setIsPaymentMethodsList] = useState(false);
  const [isTypeList, setIsTypeList] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".dropdown-toggle") &&
        !event.target.closest(".dropdown-options")
      ) {
        setIsServicesList(false);
        setIsPaymentMethodsList(false);
        setIsTypeList(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (suburbWithPostcodeStateSearchInput.length >= 2) {
      const filtered = suburbPostcodeStateList
        .filter((suburb) => {
          const text =
            `${suburb.name}, ${suburb.state_name} ${suburb.postcode}`.toLowerCase();
          return text.includes(
            suburbWithPostcodeStateSearchInput.toLowerCase()
          );
        })
        .map((suburb) => ({
          value: suburb,
          label: `${suburb.name}, ${suburb.state_name} ${suburb.postcode}`,
        }));
      setSuburbWithPostcodeStateFilteredOptions(filtered);
    } else {
      setSuburbWithPostcodeStateFilteredOptions([]);
    }
  }, [suburbWithPostcodeStateSearchInput, suburbPostcodeStateList]);

  const getPlanDetails = async () => {
    setIsLoading(true);

    try {
      const data = await fetchData("/get-pricing-plan-features", { status: 1 });
      if (data?.pricing_plan) {
        setPlanList(data.pricing_plan);
      }
      if (data?.pricing_plan_features) {
        setPlanFeaturesList(data.pricing_plan_features);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getServices = async () => {
    try {
      const data = await fetchData("/get-services", { status: 1 });
      setServicesList(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };

  const getPaymentMethods = async () => {
    try {
      const data = await fetchData("/get-master-practice-payment-methods", {
        status: 1,
      });
      setPaymentMethodsList(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };

  const getType = async () => {
    try {
      const data = await fetchData("/get-master-practice-types", { status: 1 });
      setTypeList(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };

  const getSuburbWithPostcodeState = async () => {
    try {
      const data = await fetchData("/get-suburbs-with-postcode-state");
      setSuburbPostcodeStateList(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };

  const getPostCodes = async (fk_states_id = null) => {
    try {
      const data = await fetchData("/get-postcodes", {
        status: 1,
      });
      setPostCodesList(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };

  useEffect(() => {
    if (!didFetch.current) {
      getPlanDetails();
      getServices();
      getPaymentMethods();
      getType();
      getPracticeDetails();
      getSuburbWithPostcodeState();
      getPostCodes();

      didFetch.current = true;
    }
  }, []);

  const [servicesFeesList, setServicesFeesList] = useState([]);
  const [servicesFees, setServicesFees] = useState({ name: "", fees: "" });

  const handleChangeServicesFees = (e) => {
    setServicesFees({ ...servicesFees, [e.target.name]: e.target.value });
  };

  const addServicesFees = () => {
    if (!servicesFees.name.trim()) {
      toast.error("Service name cannot be empty!");
      return;
    }
    if (!servicesFees.fees.trim()) {
      toast.error("Service fees cannot be empty!");
      return;
    }

    setServicesFeesList([...servicesFeesList, servicesFees]);
    setServicesFees({ name: "", fees: "" });
  };

  const deleteServiceFees = (index) => {
    const updatedList = servicesFeesList.filter((_, i) => i !== index);
    setServicesFeesList(updatedList);
  };

  const [faqsList, setFaqsList] = useState([]);
  const [faq, setFaq] = useState({ question: "", answer: "" });

  const handleFaqChange = (e) => {
    const { name, value } = e.target;
    setFaq((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddFaq = () => {
    if (!faq.question.trim()) {
      toast.error("FAQs question cannot be empty!");
      return;
    }
    if (!faq.answer.trim()) {
      toast.error("FAQs answer cannot be empty!");
      return;
    }

    if (faqsList.length >= currentField(13)) {
      toast.error("You cannot enter more FAQs.");
      return;
    }
    setFaqsList((prev) => [...prev, faq]);
    setFaq({ question: "", answer: "" });
  };

  const handleDeleteFaq = (index) => {
    setFaqsList((prev) => prev.filter((_, i) => i !== index));
  };

  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  const [formData, setFormData] = useState({
    fk_pricing_plan_id: "",
    fk_pricing_plan_slug: null,
    fk_states_id: null,
    fk_postcodes_id: null,
    fk_suburbs_id: null,
    practice_name: "",
    short_description: "",
    full_description: "",
    address: "",
    phone: "",
    email: "",
    add_practitioner: "0",
    image: null,
    practices_images: [],
    current_practices_images: [],
    longitude: null,
    latitude: null,
    place_id: null,
    address_url: null,
    website_link: null,
    book_appointment_link: null,
    services_ids: [],
    practices_types_ids: [],
    payment_methods_ids: [],
    practices_areas_ids: [],
    services_fees: [],
    practices_faqs: [],
  });

  // const handleImageChange = (e) => {
  //     if (e.target.files && e.target.files.length > 0) {
  //         const file = e.target.files[0];
  //         setSelectedImage(URL.createObjectURL(file));

  //         setFormData(prevState => ({
  //             ...prevState,
  //             image: file,
  //         }));
  //     }
  // };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const imageURL = URL.createObjectURL(file);
      setImageToCrop(imageURL);
      setShowCropper(true);
    }
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleServiceSelection = (serviceId) => {
    setFormData((prevState) => {
      const isSelected = prevState.services_ids.includes(serviceId);

      return {
        ...prevState,
        services_ids: isSelected
          ? prevState.services_ids.filter((id) => id !== serviceId)
          : [...prevState.services_ids, serviceId],
      };
    });

    setIsServicesList(false);
  };

  const selectedServicesText = servicesList
    .filter((service) => formData.services_ids.includes(service.services_id))
    .map((service) => service.name)
    .join(", ");

  const handleSubmit = async (e, navigateToNext) => {
    e.preventDefault();
    if (
      formData?.fk_pricing_plan_id === null ||
      formData?.fk_pricing_plan_id === ""
    ) {
      toast.error("Please select the listing type");
      return;
    }

    // if (!formData?.services_ids.length > 0) {
    //     toast.error("Please select at least one service");
    //     return;
    // }

    // if (!servicesFeesList?.length > 0) {
    //     toast.error("Please add at least one service & fees");
    //     return;
    // }

    // if (!formData?.payment_methods_ids.length > 0) {
    //     toast.error("Please add at least one billing method");
    //     return;
    // }

    // if (!formData?.practices_types_ids.length > 0) {
    //     toast.error("Please select at least one type of practice");
    //     return;
    // }

    // if (!formData?.practices_areas_ids.length > 0) {
    //     toast.error("Please select at least one service area");
    //     return;
    // }

    const {
      fk_states_id,
      fk_postcodes_id,
      fk_suburbs_id,
      address_url,
      ...restFormData
    } = formData;

    const cleanedFormData = {
      ...restFormData,
      ...(fk_states_id !== null && { fk_states_id }),
      ...(fk_postcodes_id !== null && { fk_postcodes_id }),
      ...(fk_suburbs_id !== null && { fk_suburbs_id }),
      ...(address_url !== null && { address_url }),
    };

    const payload = {
      ...cleanedFormData,
      practices_id: decrypt_practices_id,
      services_ids: formData.services_ids.join(","),
      practices_types_ids: formData.practices_types_ids.join(","),
      payment_methods_ids: formData.payment_methods_ids.join(","),
      practices_areas_ids: formData.practices_areas_ids.join(","),
      services_fees: JSON.stringify(servicesFeesList),
      practices_faqs: JSON.stringify(faqsList.slice(0, currentField(13))),
    };

    // const payload = {
    //   ...formData,
    //   practices_id: decrypt_practices_id,
    //   services_ids: formData.services_ids.join(","),
    //   practices_types_ids: formData.practices_types_ids.join(","),
    //   payment_methods_ids: formData.payment_methods_ids.join(","),
    //   practices_areas_ids: formData.practices_areas_ids.join(","),
    //   services_fees: JSON.stringify(servicesFeesList),
    //   practices_faqs: JSON.stringify(faqsList.slice(0, currentField(13))),
    // };

    const formDataToSend = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (key === "practices_images") return;
      if (key === "website_link") return;
      if (key === "book_appointment_link") return;

      formDataToSend.append(key, value);
    });

    if (currentField(14) !== 0) {
      if (payload?.website_link !== null) {
        formDataToSend.append("website_link", payload?.website_link);
      }
    }

    if (currentField(15) !== 0) {
      if (payload?.book_appointment_link !== null) {
        formDataToSend.append(
          "book_appointment_link",
          payload?.book_appointment_link
        );
      }
    }

    if (formData.current_practices_images?.length) {
      formDataToSend.append(
        "current_practices_images_ids",
        JSON.stringify(
          formData.current_practices_images
            .slice(0, currentField(12))
            .map((img) => img.practices_images_id)
        )
      );
    }

    if (formData.practices_images?.length) {
      formData.practices_images
        .slice(0, currentField(12) - formData.current_practices_images?.length)
        .forEach((file) => {
          formDataToSend.append("practices_images", file);
        });
    }

    try {
      const response = await api.post("edit-practices", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.status === true) {
        toast.success(response.data?.message);

        // setTimeout(() => {
        //     navigate('/admin');
        // }, CONSTANT_REDIRECT_TIME);

        setTimeout(() => {
          if (navigateToNext) {
            navigate(
              `/admin/practices-practitioners-add/${practices_id}/${encryptData(
                formData?.fk_pricing_plan_id
              )}/?new_practitioner=true`
            );
          } else {
            navigate("/admin");
          }
        }, CONSTANT_REDIRECT_TIME);
      } else {
        if (response?.data?.payment === true) {
          setIsPaymentModal(true);
          setPlanDetails(response?.data?.planDetails);
        } else {
          toast.error(response?.data?.message || "Failed ");
        }
        return null;
      }
    } catch (error) {
      console.error("Error editing practice:", error);
      toast.error("Something went wrong. Please try again.");
      return null;
    }
  };

  useEffect(() => {
    if (practiceDetails) {
      setFormData({
        fk_pricing_plan_id: practiceDetails.fk_pricing_plan_id || "1",
        fk_states_id: practiceDetails.fk_states_id || null,
        fk_postcodes_id: practiceDetails.fk_postcodes_id || null,
        fk_suburbs_id: practiceDetails.fk_suburbs_id || null,
        practice_name: practiceDetails.practice_name || "",
        short_description: practiceDetails.short_description || "",
        full_description: practiceDetails.full_description || "",
        address: practiceDetails.address || "",
        phone: practiceDetails.phone || "",
        email: practiceDetails.email || "",
        longitude: practiceDetails.longitude || "",
        latitude: practiceDetails.latitude || "",
        place_id: practiceDetails.place_id || null,
        address_url: practiceDetails.address_url || null,
        website_link: practiceDetails.website_link || null,
        book_appointment_link: practiceDetails.book_appointment_link || null,
        add_practitioner: practiceDetails.add_practitioner || "0",
        image: null,
        practices_images: [],
        current_practices_images: practiceDetails.practices_images || [],
        services_ids:
          practiceDetails.services?.map((service) => service.services_id) || [],
        practices_types_ids:
          practiceDetails.practices_types?.map(
            (type) => type.practices_types_master_id
          ) || [],
        payment_methods_ids:
          practiceDetails.payment_methods?.map(
            (method) => method.practices_payment_methods_master_id
          ) || [],
        practices_areas_ids:
          practiceDetails.services_areas?.map((area) => area.postcodes_id) ||
          [],
        services_fees: practiceDetails.services_fees || [],
      });

      setServicesFeesList(practiceDetails.services_fees || []);

      setFaqsList(practiceDetails.faqs || []);

      setSelectedImage(
        practiceDetails?.image ? API_BASE_URL + practiceDetails?.image : null
      );

      // if (autocompleteRef.current) {
      //   autocompleteRef.current.value = practiceDetails.address;
      // }
    }
  }, [practiceDetails]);

  const [isServicesFeesEditModalOpen, setIsServicesFeesEditModalOpen] =
    useState(false);
  const [servicesFeesEdit, setServicesFeesEdit] = useState({
    index: null,
    servicesFeesName: "",
    servicesFeesAmount: "",
  });

  const openServicesFeesEditModal = (index) => {
    const selectedServiceFee = servicesFeesList[index];
    setServicesFeesEdit({
      index,
      servicesFeesName: selectedServiceFee.name,
      servicesFeesAmount: selectedServiceFee.fees,
    });
    setIsServicesFeesEditModalOpen(true);
  };

  const handleServicesFeesEditChange = (e) => {
    const { name, value } = e.target;
    setServicesFeesEdit((prev) => ({ ...prev, [name]: value }));
  };

  const saveEditedServicesFees = (e) => {
    e.preventDefault();

    if (!servicesFeesEdit.servicesFeesName.trim()) {
      toast.error("Service name cannot be empty!");
      return;
    }
    if (!servicesFeesEdit.servicesFeesAmount.trim()) {
      toast.error("Service fee cannot be empty!");
      return;
    }

    const updatedList = [...servicesFeesList];
    updatedList[servicesFeesEdit.index] = {
      name: servicesFeesEdit.servicesFeesName,
      fees: servicesFeesEdit.servicesFeesAmount,
    };

    setServicesFeesList(updatedList);
    setIsServicesFeesEditModalOpen(false);
  };

  const selectedPlan =
    (planList.length > 0 &&
      planList.find(
        (plan) => plan.pricing_plan_id === Number(formData.fk_pricing_plan_id)
      )) ||
    null;

  const currentField = (field_id) => {
    const field = planFeaturesList.find(
      (item) => item.features_id === field_id
    );
    return field && selectedPlan?.slug
      ? Number(field[selectedPlan.slug])
      : null;
  };

  const handleImageDelete = (index) => {
    setFormData((prev) => ({
      ...prev,
      practices_images: prev.practices_images.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingImage = (id) => {
    const updated = formData.current_practices_images.filter(
      (img) => img.practices_images_id !== id
    );
    setFormData((prev) => ({
      ...prev,
      current_practices_images: updated,
    }));
  };

  if (isLoading) {
    return <Spinner adminLayout={true} />;
  }

  return (
    <>
      <AdminLayout>
        <main>
          <div className="container mx-auto px-3">
            <div className="max-w-[850px] w-full mx-auto py-10">
              <div className="flex gap-2 listing-step justify-between items-start mb-10">
                <div className="w-full max-w-[320px] md:max-w-[520px]">
                  <div className="w-full">
                    <div className="flex justify-between">
                      <div className="w-20 sm:w-[110px]">
                        <div className="relative mb-2">
                          <div className="w-7 h-7 sm:w-10 sm:h-10 mx-auto bg-blue rounded-full text-lg text-white flex items-center">
                            <span className="text-center text-white w-full flex justify-center items-center">
                              <svg
                                width="24"
                                height="17"
                                viewBox="0 0 24 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3 sm:w-5 sm:h-5"
                              >
                                <path
                                  d="M9.33293 13.2277L21.5895 0.971191L23.4751 2.85681L9.33293 16.9989L0.847656 8.5137L2.73328 6.6281L9.33293 13.2277Z"
                                  fill="white"
                                ></path>
                              </svg>
                            </span>
                          </div>
                        </div>
                        <div className="text-xs sm:text-sm text-blue font-medium text-center">
                          Practice Details
                        </div>
                      </div>
                      <div className="flex-1">
                        <hr className="border-2 border-blue top-1/2 translate-y-[-22px] sm:translate-y-[-16px] relative w-[calc(100%+52px)] sm:w-[calc(100%+70px)] mx-[-26px] sm:mx-[-35px]" />
                      </div>
                      <div className="w-20 sm:w-[110px]">
                        <div className="relative mb-2">
                          <div className="w-7 h-7 sm:w-10 sm:h-10 mx-auto border-2 border-[#B9B9B9] rounded-full text-lg  flex items-center">
                            <span className="text-center text-white w-full  flex justify-center items-center">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3 sm:w-5 sm:h-5"
                              >
                                <rect
                                  width="16"
                                  height="16"
                                  rx="8"
                                  fill="#B9B9B9"
                                />
                              </svg>
                            </span>
                          </div>
                        </div>
                        <div className="text-xs sm:text-sm text-dark font-medium text-center ">
                          Practitioners
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <form
                onSubmit={handleSubmit}
                className="grid grid-cols-2 gap-y-4 gap-x-[20px] lg:gap-x-[30px]"
              >
                <div className="col-span-2 ">
                  <label
                    htmlFor="fk_pricing_plan_id"
                    className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                  >
                    Listing Type
                  </label>
                  <div className="relative w-full mb-2">
                    <select
                      value={formData.fk_pricing_plan_id}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedPlan = planList.find(
                          (plan) =>
                            plan.pricing_plan_id.toString() === selectedId
                        );

                        setFormData({
                          ...formData,
                          fk_pricing_plan_id: selectedId,
                          fk_pricing_plan_slug: selectedPlan?.slug || null,
                        });
                      }}
                      id="fk_pricing_plan_id"
                      name="fk_pricing_plan_id"
                      className="custom-select border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 pr-10 focus:border-blue outline-none text-base sm:text-xl appearance-none"
                    >
                      {/* <option selected value={null}>Select Listing Type</option> */}
                      {planList?.length > 0 &&
                        planList?.map((plan, index) => (
                          <option key={index} value={plan?.pricing_plan_id}>
                            {plan?.name} (${plan?.price}/Year)
                          </option>
                        ))}
                      {/* {planList
                              ?.filter((plan) => plan?.pricing_plan_id === user?.fk_pricing_plan_id)
                              .map((plan, index) => (
                              <option key={index} value={plan?.pricing_plan_id}>
                                  {plan?.name} (${plan?.price}/Year)
                              </option>
                          ))} */}
                      {/* {[
                        ...planList?.filter(
                          (plan) => plan.pricing_plan_id === 1
                        ),
                        ...planList?.filter(
                          (plan) =>
                            plan.pricing_plan_id !== 1 &&
                            plan.pricing_plan_id === user?.fk_pricing_plan_id
                        ),
                      ].map((plan, index) => (
                        <option key={index} value={plan.pricing_plan_id}>
                          {plan.name} (${plan.price}/Year)
                        </option>
                      ))} */}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.0008 17.562L22.6006 10.9624L24.4862 12.848L16.0008 21.3334L7.51562 12.848L9.40124 10.9624L16.0008 17.562Z"
                          fill="black"
                        />
                      </svg>
                    </div>
                  </div>
                  <p>
                    Free listing - You cannot add your own website Link,
                    Appointment Booking link, FAQs, Photos etc.
                  </p>
                  <Link to={"/pricing"} className="text-blue" target="_blank">
                    See Pricing Plan Here.
                  </Link>
                </div>

                <div className="col-span-2">
                  <label
                    htmlFor="practice_name"
                    className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                  >
                    Practice name
                  </label>
                  <input
                    value={formData.practice_name}
                    onChange={handleChange}
                    required
                    type="text"
                    name="practice_name"
                    id="practice_name"
                    className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                    placeholder="Enter practice name"
                  />
                </div>

                {currentField(3) !== 0 && (
                  <>
                    <div className="col-span-2">
                      <label
                        htmlFor="address"
                        className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                      >
                        Search Address
                      </label>
                      <input
                        ref={autocompleteRef}
                        // value={formData.address}
                        // onChange={handleChange}
                        type="text"
                        name="search_address"
                        id="search_address"
                        className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                        placeholder="Search address"
                      />
                    </div>

                    <div className="col-span-2">
                      <label
                        htmlFor="address"
                        className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                      >
                        Practice Address
                      </label>
                      <input
                        // ref={autocompleteRef}
                        value={formData.address}
                        onChange={handleChange}
                        type="text"
                        name="address"
                        id="address"
                        className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                        placeholder="Practice address"
                      />
                    </div>
                    <div className="col-span-2">
                      <label
                        htmlFor="suburb"
                        className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                      >
                        Search Suburb or Postcode
                      </label>
                      <div className="relative w-full">
                        <Select
                          value={null}
                          onInputChange={(inputValue) => {
                            setSuburbWithPostcodeStateSearchInput(inputValue);
                          }}
                          onChange={(selectedOption) => {
                            setSelectedSuburbPostcodeState(
                              selectedOption?.value
                            );

                            setFormData((prevState) => ({
                              ...prevState,
                              fk_states_id: selectedOption?.value?.states_id,
                              fk_postcodes_id:
                                selectedOption?.value?.postcodes_id,
                              fk_suburbs_id: selectedOption?.value?.suburbs_id,
                            }));
                          }}
                          menuIsOpen={
                            suburbWithPostcodeStateSearchInput.length > 2
                          }
                          options={suburbWithPostcodeStateFilteredOptions}
                          isSearchable={true}
                          placeholder="Search Suburb or Postcode"
                          className="w-full text-black custom-select no-icon"
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label
                        for="Suburb"
                        className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                      >
                        Suburb
                      </label>
                      <p className="border-[1.2px] border-[#b5b5b5] bg-[#f5f5f5] text-[#858585] w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl flex items-center">
                        {selectedSuburbPostcodeState?.name}
                      </p>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label
                        for="Suburb"
                        className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                      >
                        State
                      </label>
                      <p className="border-[1.2px] border-[#b5b5b5] bg-[#f5f5f5] text-[#858585] w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl flex items-center">
                        {selectedSuburbPostcodeState?.state_name}
                      </p>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label
                        for="Suburb"
                        className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                      >
                        Postcode
                      </label>
                      <p className="border-[1.2px] border-[#b5b5b5] bg-[#f5f5f5] text-[#858585] w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl flex items-center">
                        {selectedSuburbPostcodeState?.postcode}
                      </p>
                    </div>
                  </>
                )}

                <div className="col-span-2">
                  <label
                    htmlFor="short_description"
                    className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                  >
                    Short Description{" "}
                    <span className="font-normal text-sm">
                      (max. {100 - formData?.short_description?.length}{" "}
                      characters)
                    </span>
                  </label>
                  <textarea
                    value={formData.short_description}
                    onChange={handleChange}
                    maxLength={100}
                    name="short_description"
                    id="short_description"
                    placeholder="Enter Description"
                    className="border-[1.2px] border-dark w-full  rounded-xl py-4 px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl min-h-32"
                    spellCheck="false"
                  ></textarea>
                </div>

                {currentField(4) !== 0 && (
                  <div className="col-span-2">
                    <label
                      htmlFor="phone"
                      className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                    >
                      Phone Number
                    </label>
                    <input
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (/^\+?[0-9]*$/.test(value) &&
                            value.replace(/^\+/, "").length <= 10)
                        ) {
                          handleChange(e);
                        }
                      }}
                      type="text"
                      name="phone"
                      id="phone"
                      className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                      placeholder="Enter phone number"
                      pattern="^\+?[0-9]{1,10}$"
                    />
                  </div>
                )}

                <div className="col-span-2">
                  <label
                    htmlFor="email"
                    className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                  >
                    Practice Email
                  </label>
                  <input
                    value={formData.email}
                    onChange={handleChange}
                    type="text"
                    name="email"
                    id="email"
                    className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                    placeholder="Enter email id"
                  />
                </div>

                {currentField(2) !== 0 && (
                  <div className="col-span-2">
                    <label
                      htmlFor="image"
                      className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                    >
                      Practice Profile Picture{" "}
                      <span className="text-sm text-gray-500 font-normal">
                        (Recommended size - 170 pixels wide and 170 pixels
                        tall.)
                      </span>
                    </label>

                    <label
                      htmlFor="image"
                      className="flex flex-col items-center justify-center w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100"
                    >
                      {selectedImage ? (
                        <img
                          src={selectedImage}
                          alt="Selected Preview"
                          className="w-28 h-28 object-cover rounded-lg"
                        />
                      ) : (
                        <span className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 12.5858L16.2426 16.8284L14.8284 18.2426L13 16.415V22H11V16.413L9.17157 18.2426L7.75736 16.8284L12 12.5858ZM12 2C15.5934 2 18.5544 4.70761 18.9541 8.19395C21.2858 8.83154 23 10.9656 23 13.5C23 16.3688 20.8036 18.7246 18.0006 18.9776L18.0009 16.9644C19.6966 16.7214 21 15.2629 21 13.5C21 11.567 19.433 10 17.5 10C17.2912 10 17.0867 10.0183 16.8887 10.054C16.9616 9.7142 17 9.36158 17 9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9C7 9.36158 7.03838 9.7142 7.11205 10.0533C6.91331 10.0183 6.70879 10 6.5 10C4.567 10 3 11.567 3 13.5C3 15.2003 4.21241 16.6174 5.81986 16.934L6.00005 16.9646L6.00039 18.9776C3.19696 18.7252 1 16.3692 1 13.5C1 10.9656 2.71424 8.83154 5.04648 8.19411C5.44561 4.70761 8.40661 2 12 2Z"
                              fill="#00A167"
                            />
                          </svg>
                          <span className="text-sm font-semibold text-green">
                            Browse Image
                          </span>
                        </span>
                      )}
                      <input
                        id="image"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageChange}
                        name="image"
                      />
                    </label>

                    {showCropper && (
                      <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4">
                        <div className="relative w-full max-w-lg bg-white rounded-lg overflow-hidden shadow-lg">
                          {/* Cropper area */}
                          <div className="relative w-full h-[400px] bg-gray-200">
                            <Cropper
                              image={imageToCrop}
                              crop={crop}
                              zoom={zoom}
                              aspect={1}
                              onCropChange={setCrop}
                              onZoomChange={setZoom}
                              onCropComplete={onCropComplete}
                            />
                          </div>

                          {/* Zoom Slider */}
                          <div className="px-6 pt-4">
                            <Slider
                              value={zoom}
                              min={1}
                              max={3}
                              step={0.1}
                              onChange={(e, value) => setZoom(value)}
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex justify-end gap-4 px-6 py-4 border-t border-gray-200 bg-white">
                            <button
                              type="button"
                              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                              onClick={() => setShowCropper(false)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="py-3 px-4 sm:px-8 border-green  border-2 border-solid bg-green text-white rounded-[8px] font-medium sm:font-semibold text-sm sm:text-base leading-none text-center text-nowrap"
                              onClick={async () => {
                                const croppedImage = await getCroppedImg(
                                  imageToCrop,
                                  croppedAreaPixels
                                );
                                setSelectedImage(croppedImage);

                                const file = await fetch(croppedImage)
                                  .then((r) => r.blob())
                                  .then(
                                    (blob) =>
                                      new File([blob], "cropped.jpg", {
                                        type: "image/jpeg",
                                      })
                                  );

                                setFormData((prev) => ({
                                  ...prev,
                                  image: file,
                                }));
                                setShowCropper(false);
                              }}
                            >
                              Crop & Save
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="col-span-2">
                  <label
                    htmlFor="full_description"
                    className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                  >
                    Overview{" "}
                    <span className="font-normal text-sm">
                      (Full Description)
                    </span>
                  </label>
                  <textarea
                    value={formData.full_description}
                    onChange={handleChange}
                    name="full_description"
                    id="full_description"
                    placeholder="Enter description"
                    className="border-[1.2px] border-dark w-full  rounded-xl py-4 px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl min-h-32"
                    spellCheck="false"
                  ></textarea>
                </div>

                {currentField(6) !== 0 && (
                  <div className="col-span-2">
                    <label className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl">
                      Services
                    </label>
                    <div className="relative w-full multi-select-dropdown">
                      <div
                        className="dropdown-box relative border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 pr-10 flex items-center justify-between cursor-pointer focus:border-blue outline-none text-base sm:text-xl bg-white dropdown-toggle"
                        onClick={() => setIsServicesList(!isServicesList)}
                      >
                        <span className="selected-options text-ellipsis line-clamp-1">
                          {selectedServicesText || "Select service"}
                        </span>
                        <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                          <svg
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16.0008 17.562L22.6006 10.9624L24.4862 12.848L16.0008 21.3334L7.51562 12.848L9.40124 10.9624L16.0008 17.562Z"
                              fill="black"
                            />
                          </svg>
                        </div>
                      </div>
                      <div
                        className={`dropdown-options absolute w-full bg-white border border-gray-300 rounded-xl mt-1 shadow-md z-10 max-h-96 overflow-y-auto ${
                          isServicesList ? "" : "hidden"
                        }`}
                      >
                        {servicesList?.length > 0 &&
                          servicesList?.map((services, index) => (
                            <label
                              key={index}
                              className="flex items-center px-4 py-2 cursor-pointer text-black text-base sm:text-xl"
                            >
                              <input
                                type="checkbox"
                                value={services?.services_id}
                                className="practice-checkbox w-4 h-4 mr-2"
                                checked={formData.services_ids.includes(
                                  services.services_id
                                )}
                                onChange={() =>
                                  handleServiceSelection(services.services_id)
                                }
                              />
                              {services?.name}
                            </label>
                          ))}
                      </div>
                    </div>
                  </div>
                )}

                {currentField(5) !== 0 && (
                  <div className="col-span-2">
                    <label className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl">
                      Service & Fees
                    </label>
                    <div className="grid grid-cols-2 gap-4 flex-wrap">
                      <div className="col-span-2 md:col-span-1 ">
                        <input
                          type="text"
                          value={servicesFees.name}
                          onChange={handleChangeServicesFees}
                          name="name"
                          className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                          placeholder="Enter Service"
                        />
                      </div>
                      <div className="col-span-2 md:col-span-1 flex space-x-4">
                        <input
                          value={servicesFees.fees}
                          onChange={handleChangeServicesFees}
                          name="fees"
                          type="text"
                          className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                          placeholder="Enter Fees"
                        />
                        <button
                          type="button"
                          onClick={addServicesFees}
                          className="bg-blue text-white px-4 sm:px-6 w-2/5 h-12 sm:h-14 rounded-xl"
                        >
                          Save
                        </button>
                      </div>
                    </div>

                    {servicesFeesList?.length > 0 && (
                      <div
                        id="serviceListContainer"
                        className="mt-4 overflow-x-auto"
                      >
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-lightblue">
                              <th className="border-b border-gray-300 p-2 text-left text-blue font-semibold min-w-[180px] w-full">
                                Service
                              </th>
                              <th className="border-b border-gray-300 p-2 text-left text-blue font-semibold min-w-[120px]">
                                Fee ({CONSTANT_CURRENCY})
                              </th>
                              <th className="border-b border-gray-300 p-2 text-center  text-blue font-semibold min-w-[104px]">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody id="serviceList">
                            {servicesFeesList?.map((item, index) => (
                              <tr key={index}>
                                <td className="border-b border-gray-300 p-2">
                                  {item?.name}
                                </td>
                                <td className="border-b border-gray-300 p-2">
                                  {item?.fees}
                                </td>
                                <td className="border-b border-gray-300 p-2 text-center">
                                  <div
                                    className="flex rounded-full shadow-xs bg-blue max-w-[120px] w-full"
                                    role="group"
                                  >
                                    <button
                                      type="button"
                                      className="pl-4 pr-2 py-2 text-sm font-medium  bg-transparent rounded-s-full"
                                      onClick={() =>
                                        openServicesFeesEditModal(index)
                                      }
                                    >
                                      <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        className="w-5 h-5"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M5 18.89H6.41421L15.7279 9.57628L14.3137 8.16207L5 17.4758V18.89ZM21 20.89H3V16.6473L16.435 3.21232C16.8256 2.8218 17.4587 2.8218 17.8492 3.21232L20.6777 6.04075C21.0682 6.43127 21.0682 7.06444 20.6777 7.45496L9.24264 18.89H21V20.89ZM15.7279 6.74786L17.1421 8.16207L18.5563 6.74786L17.1421 5.33364L15.7279 6.74786Z"
                                          fill="white"
                                        ></path>
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => deleteServiceFees(index)}
                                      type="button"
                                      className="pl-2 pr-4 py-2 text-sm font-medium  bg-transparent rounded-e-full"
                                    >
                                      <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        className="w-5 h-5"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M7 4V2H17V4H22V6H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V6H2V4H7ZM6 6V20H18V6H6ZM9 9H11V17H9V9ZM13 9H15V17H13V9Z"
                                          fill="white"
                                        ></path>
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                <div className="col-span-2">
                  <label
                    htmlFor="payment_methods_ids"
                    className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                  >
                    Accepted Fees through
                  </label>
                  <div className="relative w-full multi-select-dropdown">
                    <div
                      className="dropdown-box relative border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 pr-10 flex items-center justify-between cursor-pointer focus:border-blue outline-none text-base sm:text-xl bg-white dropdown-toggle"
                      onClick={() =>
                        setIsPaymentMethodsList(!isPaymentMethodsList)
                      }
                    >
                      <span className="selected-options text-ellipsis line-clamp-1">
                        {formData.payment_methods_ids.length > 0
                          ? paymentMethodsList
                              .filter((method) =>
                                formData.payment_methods_ids.includes(
                                  method.practices_payment_methods_master_id
                                )
                              )
                              .map((method) => method.name)
                              .join(", ")
                          : "Select billing"}
                      </span>
                      <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.0008 17.562L22.6006 10.9624L24.4862 12.848L16.0008 21.3334L7.51562 12.848L9.40124 10.9624L16.0008 17.562Z"
                            fill="black"
                          />
                        </svg>
                      </div>
                    </div>

                    <div
                      className={`dropdown-options absolute w-full bg-white border border-gray-300 rounded-xl mt-1 shadow-md z-10 max-h-96 overflow-y-auto ${
                        isPaymentMethodsList ? "" : "hidden"
                      }`}
                    >
                      {paymentMethodsList?.length > 0 &&
                        paymentMethodsList.map((paymentMethod) => (
                          <label
                            key={
                              paymentMethod.practices_payment_methods_master_id
                            }
                            className="flex items-center px-4 py-2 cursor-pointer text-black text-base sm:text-xl"
                          >
                            <input
                              type="checkbox"
                              value={
                                paymentMethod.practices_payment_methods_master_id
                              }
                              className="practice-checkbox mr-2 w-4 h-4"
                              checked={formData.payment_methods_ids.includes(
                                paymentMethod.practices_payment_methods_master_id
                              )}
                              onChange={() => {
                                setFormData((prevState) => {
                                  const isSelected =
                                    prevState.payment_methods_ids.includes(
                                      paymentMethod.practices_payment_methods_master_id
                                    );
                                  return {
                                    ...prevState,
                                    payment_methods_ids: isSelected
                                      ? prevState.payment_methods_ids.filter(
                                          (id) =>
                                            id !==
                                            paymentMethod.practices_payment_methods_master_id
                                        )
                                      : [
                                          ...prevState.payment_methods_ids,
                                          paymentMethod.practices_payment_methods_master_id,
                                        ],
                                  };
                                });
                                setIsPaymentMethodsList(false);
                              }}
                            />
                            {paymentMethod.name}
                          </label>
                        ))}
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl">
                    Type of Practice
                  </label>
                  <div className="relative w-full multi-select-dropdown">
                    {/* Dropdown Box */}
                    <div
                      className="dropdown-box relative border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 pr-10 flex items-center justify-between cursor-pointer focus:border-blue outline-none text-base sm:text-xl bg-white dropdown-toggle"
                      onClick={() => setIsTypeList(!isTypeList)}
                    >
                      <span className="selected-options text-ellipsis line-clamp-1">
                        {formData.practices_types_ids.length > 0
                          ? typeList
                              .filter((type) =>
                                formData.practices_types_ids.includes(
                                  type.practices_types_master_id
                                )
                              )
                              .map((type) => type.name)
                              .join(", ")
                          : "Select practice type"}
                      </span>
                      <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.0008 17.562L22.6006 10.9624L24.4862 12.848L16.0008 21.3334L7.51562 12.848L9.40124 10.9624L16.0008 17.562Z"
                            fill="black"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Dropdown Options */}
                    <div
                      className={`dropdown-options absolute w-full bg-white border border-gray-300 rounded-xl mt-1 shadow-md z-10 max-h-96 overflow-y-auto ${
                        isTypeList ? "" : "hidden"
                      }`}
                    >
                      {typeList?.length > 0 &&
                        typeList.map((type) => (
                          <label
                            key={type.practices_types_master_id}
                            className="flex items-center px-4 py-2 cursor-pointer text-black text-base sm:text-xl"
                          >
                            <input
                              type="checkbox"
                              value={type.practices_types_master_id}
                              className="practice-checkbox mr-2 w-4 h-4"
                              checked={formData.practices_types_ids.includes(
                                type.practices_types_master_id
                              )}
                              onChange={() => {
                                setFormData((prevState) => {
                                  const isSelected =
                                    prevState.practices_types_ids.includes(
                                      type.practices_types_master_id
                                    );
                                  return {
                                    ...prevState,
                                    practices_types_ids: isSelected
                                      ? prevState.practices_types_ids.filter(
                                          (id) =>
                                            id !==
                                            type.practices_types_master_id
                                        )
                                      : [
                                          ...prevState.practices_types_ids,
                                          type.practices_types_master_id,
                                        ],
                                  };
                                });
                                setIsTypeList(false);
                              }}
                            />
                            {type.name}
                          </label>
                        ))}
                    </div>
                  </div>
                </div>
                
                {formData.practices_types_ids.includes(2) && (
                  <div className="col-span-2">
                    <label
                      htmlFor="practices_areas_ids"
                      className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                    >
                      Servicing Areas
                    </label>

                    <Select
                      options={
                        Array.isArray(postCodesList) &&
                        postCodesList.map((item) => ({
                          value: item.postcodes_id,
                          label: item.postcode,
                        }))
                      }
                      isMulti
                      className="w-full text-black custom-select"
                      placeholder="Enter postcodes"
                      value={
                        Array.isArray(postCodesList) &&
                        postCodesList
                          .filter((item) =>
                            formData.practices_areas_ids.includes(
                              item.postcodes_id
                            )
                          )
                          .map((item) => ({
                            value: item.postcodes_id,
                            label: item.postcode,
                          }))
                      }
                      onChange={(selectedOptions) => {
                        setFormData((prevState) => ({
                          ...prevState,
                          practices_areas_ids: selectedOptions.map(
                            (option) => option.value
                          ),
                        }));
                      }}
                    />
                  </div>
                )}

                {currentField(14) !== 0 && (
                  <div className="col-span-2">
                    <label
                      htmlFor="website_link"
                      className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                    >
                      Website
                    </label>
                    <input
                      value={formData.website_link}
                      onChange={handleChange}
                      required
                      type="url"
                      name="website_link"
                      id="website_link"
                      className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                      placeholder="Enter website URL"
                    />
                  </div>
                )}

                {currentField(15) !== 0 && (
                  <div className="col-span-2">
                    <label
                      htmlFor="book_appointment_link"
                      className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                    >
                      Book Appointment Link
                    </label>
                    <input
                      value={formData.book_appointment_link}
                      onChange={handleChange}
                      required
                      type="url"
                      name="book_appointment_link"
                      id="book_appointment_link"
                      className="border-[1.2px] border-dark w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl"
                      placeholder="Enter appointment link"
                    />
                  </div>
                )}

                {currentField(13) !== 0 && (
                  <div className="col-span-2">
                    <label className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl">
                      FAQs
                    </label>

                    {/* Input Block */}
                    <div className="border-[1.2px] border-dark w-full p-4 rounded-xl faq-wrapper mb-2">
                      <div className="flex flex-wrap gap-3">
                        <input
                          type="text"
                          name="question"
                          value={faq.question}
                          onChange={handleFaqChange}
                          className="border-[1.2px] border-dark w-[calc(100%-12px)] sm:w-full h-12 sm:h-14 rounded-xl px-4 sm:px-7 focus:border-blue outline-none text-lg sm:text-xl"
                          placeholder="Enter FAQ Question"
                        />
                        <textarea
                          name="answer"
                          value={faq.answer}
                          onChange={handleFaqChange}
                          placeholder="Enter FAQ Answer"
                          className="border-[1.2px] border-dark w-full rounded-xl py-4 px-4 sm:px-7 focus:border-blue outline-none text-base sm:text-xl min-h-32"
                        ></textarea>
                      </div>
                    </div>

                    {/* Add FAQ Button */}
                    <button
                      type="button"
                      onClick={handleAddFaq}
                      className="addfaq py-4 px-6 rounded bg-green text-white text-base lg:text-xl leading-none font-medium mt-3 ml-auto block"
                    >
                      Add FAQ
                    </button>

                    {/* Display FAQ List */}
                    {faqsList?.length > 0 &&
                      faqsList.map((item, index) => (
                        <div
                          key={index}
                          className="border-[1.2px] border-dark w-full p-4 rounded-xl faq-wrapper mt-4"
                        >
                          <div className="flex justify-between items-start">
                            <div className="w-full">
                              <p className="font-semibold text-base sm:text-lg">
                                {item.question}
                              </p>
                              <p className="text-sm sm:text-base mt-2">
                                {item.answer}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteFaq(index)}
                              className="delete-faq ml-4 border-[1.2px] border-red-600 bg-red-600 w-10 h-10 rounded-xl flex items-center justify-center"
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M22.668 7.99984H29.3346V10.6665H26.668V27.9998C26.668 28.7362 26.071 29.3332 25.3346 29.3332H6.66797C5.9316 29.3332 5.33464 28.7362 5.33464 27.9998V10.6665H2.66797V7.99984H9.33464V3.99984C9.33464 3.26346 9.93159 2.6665 10.668 2.6665H21.3346C22.071 2.6665 22.668 3.26346 22.668 3.99984V7.99984ZM24.0013 10.6665H8.0013V26.6665H24.0013V10.6665ZM12.0013 14.6665H14.668V22.6665H12.0013V14.6665ZM17.3346 14.6665H20.0013V22.6665H17.3346V14.6665ZM12.0013 5.33317V7.99984H20.0013V5.33317H12.0013Z"
                                  fill="white"
                                ></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {currentField(12) !== 0 && (
                  <div className="col-span-2 free-plan">
                    <label
                      htmlFor="photos"
                      className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                    >
                      Photos{" "}
                      <span className="font-normal text-sm">
                        (max {currentField(12)} photos upload)
                      </span>
                    </label>

                    <label
                      htmlFor="photos"
                      className="flex flex-col items-center justify-center w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100"
                    >
                      <span className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 12.5858L16.2426 16.8284L14.8284 18.2426L13 16.415V22H11V16.413L9.17157 18.2426L7.75736 16.8284L12 12.5858ZM12 2C15.5934 2 18.5544 4.70761 18.9541 8.19395C21.2858 8.83154 23 10.9656 23 13.5C23 16.3688 20.8036 18.7246 18.0006 18.9776L18.0009 16.9644C19.6966 16.7214 21 15.2629 21 13.5C21 11.567 19.433 10 17.5 10C17.2912 10 17.0867 10.0183 16.8887 10.054C16.9616 9.7142 17 9.36158 17 9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9C7 9.36158 7.03838 9.7142 7.11205 10.0533C6.91331 10.0183 6.70879 10 6.5 10C4.567 10 3 11.567 3 13.5C3 15.2003 4.21241 16.6174 5.81986 16.934L6.00005 16.9646L6.00039 18.9776C3.19696 18.7252 1 16.3692 1 13.5C1 10.9656 2.71424 8.83154 5.04648 8.19411C5.44561 4.70761 8.40661 2 12 2Z"
                            fill="#00A167"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-green">
                          Browse Image
                        </span>
                      </span>

                      <input
                        id="photos"
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        name="practices_images"
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          const imagesCount =
                            (formData.practices_images?.length || 0) +
                            files?.length +
                            formData.current_practices_images.length;
                          if (imagesCount > currentField(12)) {
                            toast.error(
                              `You can only upload a maximum of ${currentField(
                                12
                              )} photos.`
                            );
                            return;
                          }
                          setFormData((prev) => ({
                            ...prev,
                            practices_images: [
                              ...prev.practices_images,
                              ...files,
                            ],
                          }));
                        }}
                      />
                    </label>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {/* Optional Preview */}

                      {formData.current_practices_images.length > 0 && (
                        <>
                          {formData.current_practices_images.map(
                            (img, index) => (
                              <div
                                key={`existing-${img.practices_images_id}`}
                                className="relative"
                              >
                                <img
                                  src={API_BASE_URL + `${img.image_url}`}
                                  alt={`Image ${index}`}
                                  className="w-20 h-20 object-cover rounded-lg border"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRemoveExistingImage(
                                      img.practices_images_id
                                    )
                                  }
                                  className="absolute top-0 right-0 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                >
                                  ×
                                </button>
                              </div>
                            )
                          )}
                        </>
                      )}

                      {formData?.practices_images?.length > 0 && (
                        <>
                          {formData.practices_images.map((file, index) => (
                            <div key={index} className="relative w-20 h-20">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`Preview ${index}`}
                                className="w-full h-full object-cover rounded-lg border"
                              />
                              <button
                                type="button"
                                onClick={() => handleImageDelete(index)}
                                className="absolute top-0 right-0 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {currentField(10) !== 0 && (
                  <div className="col-span-2">
                    <label
                      htmlFor="add_practitioner"
                      className="mb-2 inline-block text-dark font-semibold text-base sm:text-xl"
                    >
                      Add Practitioner
                    </label>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <input
                          type="radio"
                          id="add_practitioner_1"
                          name="add_practitioner"
                          value="1"
                          checked={formData.add_practitioner == "1"}
                          onChange={handleChange}
                        />
                        <label htmlFor="add_practitioner_1">Yes</label>
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="radio"
                          id="add_practitioner_0"
                          name="add_practitioner"
                          value="0"
                          checked={formData.add_practitioner == "0"}
                          onChange={handleChange}
                        />
                        <label htmlFor="add_practitioner_0">No</label>
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-span-2 flex flex-wrap gap-1 justify-between mt-8">
                  <button
                    onClick={(e) => handleSubmit(e, false)}
                    type="button"
                    className="py-4 px-6 rounded bg-green text-white text-base lg:text-xl leading-none font-medium"
                  >
                    Save & Exit
                  </button>

                  {currentField(10) !== 0 && (
                    <>
                      {formData?.add_practitioner == "1" && (
                        <button
                          onClick={(e) => handleSubmit(e, true)}
                          type="button"
                          className="py-4 px-6 rounded bg-green text-white text-base lg:text-xl leading-none font-medium"
                          id="practitionersbtn"
                        >
                          Add Practitioner
                        </button>
                      )}
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </main>

        <div
          id="servicesFeesUpdate"
          className={`fixed z-[999] inset-0 flex items-center justify-center bg-black bg-opacity-50 px-3 ${
            isServicesFeesEditModalOpen ? "block" : "hidden"
          }`}
        >
          <div className="emailbox bg-white p-9 rounded-lg shadow-lg w-full max-w-[512px] relative max-h-[90vh]">
            <button
              className="absolute right-4 top-4"
              onClick={() => setIsServicesFeesEditModalOpen(false)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.41791 0.436139C0.685579 0.168311 1.04857 0.0178675 1.42704 0.0178675C1.80551 0.0178675 2.1685 0.168311 2.43617 0.436139L9.99103 7.99754L17.5459 0.436139C17.6777 0.299682 17.8351 0.190853 18.0092 0.115982C18.1834 0.0411107 18.3708 0.00171073 18.5602 5.3593e-05C18.7497 -0.00158926 18.9377 0.0345535 19.1131 0.106382C19.2886 0.17821 19.4479 0.284296 19.5819 0.418424C19.7159 0.552553 19.822 0.712052 19.8938 0.887623C19.9654 1.06319 20.0015 1.25131 19.9999 1.44099C19.9982 1.63068 19.9588 1.81814 19.884 1.99242C19.8093 2.16671 19.7005 2.32435 19.5642 2.45612L12.0093 10.0175L19.5642 17.5789C19.8242 17.8484 19.9681 18.2092 19.9648 18.5838C19.9616 18.9584 19.8115 19.3167 19.5468 19.5815C19.2821 19.8464 18.9242 19.9967 18.5499 19.9999C18.1757 20.0032 17.8151 19.8592 17.5459 19.5989L9.99103 12.0375L2.43617 19.5989C2.16697 19.8592 1.80643 20.0032 1.43218 19.9999C1.05793 19.9967 0.699938 19.8464 0.435296 19.5815C0.170653 19.3167 0.0205397 18.9584 0.0172997 18.5838C0.0140454 18.2092 0.157906 17.8484 0.41791 17.5789L7.97277 10.0175L0.41791 2.45612C0.150327 2.18824 0 1.82494 0 1.44612C0 1.06732 0.150327 0.704024 0.41791 0.436139Z"
                  fill="black"
                ></path>
              </svg>
            </button>
            <form onSubmit={saveEditedServicesFees} className="space-y-3">
              <div>
                <label className="mb-2 inline-block text-dark font-semibold text-base">
                  Service Name
                </label>
                <input
                  type="text"
                  name="servicesFeesName"
                  value={servicesFeesEdit.servicesFeesName}
                  onChange={handleServicesFeesEditChange}
                  className="border-[1.2px] border-dark w-full h-10 sm:h-12 rounded-xl px-3 sm:px-4 focus:border-blue outline-none text-lg"
                  placeholder="Enter service name"
                />
              </div>
              <div>
                <label className="mb-2 inline-block text-dark font-semibold text-base">
                  Service Fee
                </label>
                <input
                  type="text"
                  name="servicesFeesAmount"
                  value={servicesFeesEdit.servicesFeesAmount}
                  onChange={handleServicesFeesEditChange}
                  className="border-[1.2px] border-dark w-full h-10 sm:h-12 rounded-xl px-3 sm:px-4 focus:border-blue outline-none text-lg"
                  placeholder="Enter service fee"
                />
              </div>
              <button
                type="submit"
                className="py-3 px-3 bg-blue w-full text-white font-semibold text-lg rounded-xl"
              >
                Save
              </button>
            </form>
          </div>
        </div>

        <PaymentModal
          setIsPaymentModal={setIsPaymentModal}
          isPaymentModal={isPaymentModal}
          planDetails={planDetails}
          setPlanDetails={setPlanDetails}
          handlePracticesSubmit={handleSubmit}
        />
      </AdminLayout>
    </>
  );
};

export default AdminPracticesEdit;
